import { Table, Button, Row, Col, Form, Pagination } from 'react-bootstrap';
import { FaEdit, FaPlus, FaTrash, FaDownload } from 'react-icons/fa';
import { Link, } from 'react-router-dom';
import Message from '../../components/Message';
import Loader from '../../components/Loader';

import { useGetProductsQuery, useDeleteProductMutation, useCreateProductMutation } from '../../slices/productsApiSlice';
import { toast } from 'react-toastify';
import { useState } from 'react';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
  

const ProductListScreen = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const { data, isLoading, error, refetch } = useGetProductsQuery({ pageNumber });
  const [deleteProduct, { isLoading: loadingDelete }] = useDeleteProductMutation();
  const [createProduct, { isLoading: loadingCreate }] = useCreateProductMutation();

  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure')) {
      try {
        await deleteProduct(id);
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };
  const createProductHandler = async () => {
    if (window.confirm('Are you sure you want to create a new product?')) {
      try {
        await createProduct();
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };
  
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(order);
  };

  const filterAndSortProducts = () => {
    let filteredProducts = data.products;

    // Filter products
    if (filter) {
      filteredProducts = filteredProducts.filter(
        (product) =>
          product.name?.toLowerCase().includes(filter.toLowerCase()) ||
          product.brand?.toLowerCase().includes(filter.toLowerCase()) ||
          product.category?.toLowerCase().includes(filter.toLowerCase())
      );
    }

    // Sort products
    if (sortField) {
      filteredProducts.sort((a, b) => {
        if (sortField === 'price') {
          return sortOrder === 'asc' ? a.price - b.price : b.price - a.price;
        } else {
          return sortOrder === 'asc'
            ? a[sortField].localeCompare(b[sortField])
            : b[sortField].localeCompare(a[sortField]);
        }
      });
    }

    return filteredProducts;
  };

  
  const downloadProductImages = async (product) => {
    const zip = new JSZip();
    const folder = zip.folder(`Product_${product.name}`);

    const imagePromises = product.images.map(async (imagePath, index) => {
      try {
        const response = await fetch(imagePath.replace(/\\/g, '/')); // Fix path formatting
        const blob = await response.blob();
        const urlParts = imagePath.split('/');
        const filename = urlParts[urlParts.length - 1]; // Use original filename

        folder.file(filename, blob);
      } catch (error) {
        console.error(`Failed to fetch ${imagePath}`, error);
      }
    });

    await Promise.all(imagePromises);
    zip.generateAsync({ type: 'blob' }).then((zipFile) => {
      saveAs(zipFile, `Product_${product.name}_Images.zip`);
    });
  };

  const downloadAllImages = async () => {
    const zip = new JSZip();
    const folder = zip.folder('All_Products_Images');

    const imagePromises = data.products.flatMap((product) =>
      product.images.map(async (imagePath, index) => {
        try {
          const response = await fetch(imagePath.replace(/\\/g, '/')); // Fix path formatting
          const blob = await response.blob();
          const urlParts = imagePath.split('/');
          const filename = urlParts[urlParts.length - 1];

          folder.file(filename, blob); // Unique naming per product
        } catch (error) {
          console.error(`Failed to fetch ${imagePath}`, error);
        }
      })
    );

    await Promise.all(imagePromises);
    zip.generateAsync({ type: 'blob' }).then((zipFile) => {
      saveAs(zipFile, 'All_Products_Images.zip');
    });
  };
  const exportToExcel = () => {
    if (!data || !data.products || data.products.length === 0) {
      toast.error("No products available to export!");
      return;
    }
  
    const productsData = data.products.map((product) => ({
      Id: product._id,
      Name: product.name,
      Brand: product.brand,
      Category: product.category,
      Price: `${product.price.toFixed(2)}`,
      Stock: product.countInStock,
      Image: product.images[0],
    }));
  
    const worksheet = XLSX.utils.json_to_sheet(productsData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Products');
  
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const file = new Blob([excelBuffer], { type: 'application/octet-stream' });
  
    saveAs(file, 'Products.xlsx');
  };
  // Handle pagination changes
  const paginationHandler = (page) => {
    setPageNumber(page);
  };

  return (
    <>
      <Row className="align-items-center">
        <Col>
          <h1>Products</h1>
        </Col>
        <Col className="text-end">
          <Button className="my-3 mx-2" onClick={downloadAllImages}>
            <FaDownload /> Download All Images
          </Button>
          <Button className="my-3 mx-2" onClick={exportToExcel}>
         <FaDownload /> Export to Excel
         </Button>
          <Button className="my-3" onClick={createProductHandler}>
            <FaPlus /> Create Product
          </Button>
          <Link to='/create/product/excelform' className='btn btn-light my-3'>
          AddFromExcel
          </Link> 
          <Link to='/update/product/excelform' className='btn btn-light my-3'>
          UpdateFromExcel
          </Link>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <Form.Control type="text" placeholder="Filter by name, brand, or category" value={filter} onChange={(e) => setFilter(e.target.value)} />
        </Col>
      </Row>

      {loadingCreate && <Loader />}
      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error.data.message}</Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">

            <thead>
              <tr>
                
                <th
                  onClick={() => handleSort('name')}
                  style={{ cursor: 'pointer' }}
                >
                  NAME {sortField === 'name' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th
                  onClick={() => handleSort('price')}
                  style={{ cursor: 'pointer' }}
                >
                  PRICE {sortField === 'price' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th
                  onClick={() => handleSort('category')}
                  style={{ cursor: 'pointer' }}
                >
                  CATEGORY {sortField === 'category' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th
                  onClick={() => handleSort('brand')}
                  style={{ cursor: 'pointer' }}
                >
                  BRAND {sortField === 'brand' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th>IMAGE</th>
                <th>ACTION</th>

              </tr>
            </thead>

            <tbody>
              {filterAndSortProducts().map((product) => (
                <tr key={product._id}>
                  
                  <td>{product.name}</td>
                  <td>${product.price.toFixed(2)}</td>
                  <td>{product.category}</td>
                  <td>{product.brand}</td>
                  <td className="thumbnails">
                    {product.images?.length > 0 && (
                        <img
                        
                        src={product.images[0]}
                        alt="Thumbnail"
                        className="thumbnail-img me-2"
                        style={{
                            width: '15px',
                            height: '15px',
                            objectFit: 'cover',
                        }}
                        />
                    )}
                    </td>
                   <td>
                    <Button as={Link} to={`/admin/product/${product._id}/edit`} variant="light" className="btn-sm mx-2">
                      <FaEdit />
                    </Button>
                    <Button variant="danger" className="btn-sm mx-2" onClick={() => deleteHandler(product._id)}>
                      <FaTrash style={{ color: 'white' }} />
                    </Button>
                    <Button variant="info" className="btn-sm" onClick={() => downloadProductImages(product)}>
                      <FaDownload />Images
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
         <Pagination>
                      {[...Array(data.pages).keys()].map((x) => (
                      <Pagination.Item
                      key={x + 1}
                      active={x + 1 === pageNumber}
                      onClick={() => paginationHandler(x + 1)}
                     >
                      {x + 1}
                    </Pagination.Item>
                    ))}
         </Pagination>
          
        </>
      )}
    </>
  );
};

export default ProductListScreen;